import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils/user";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import { getLevels } from "sj-ui-toolkit/src/api";
import { chartColors } from "sj-ui-toolkit/src/atlas/utils";
import {
  DEFAULT_ROW_COUNT,
  ROWS_PER_PAGE_OPTIONS,
} from "sj-ui-toolkit/src/constants/tableRowsCounts";
import { DataTable } from "primereact/datatable";
import type { DataTableValueArray } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { Skeleton } from "primereact/skeleton";
import { FilterMatchMode } from "primereact/api";
import { isEmpty } from "lodash";
import { loadLevelsVP, loadLessonsVP, exportRankingsCSV } from "@/api/reports";
import type { LessonLevelData, DojoLevelData, CourseData, LessonData } from "@/api/reports";
import { withSkeleton, skeletonRows } from "@/helpers/tableBody";
import { textFilterElement } from "@/helpers/tableFilters";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RANKINGS_EMPTY_ROW_TEXT = "No rankings data found.";

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: { display: false },
      ticks: { display: false },
      border: { display: false },
    },
    x: {
      grid: { display: false },
      ticks: { display: false },
      border: { display: false },
    },
  },
};

const barDatasetOptions = {
  barPercentage: 0.95,
  categoryPercentage: 0.99,
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CoursesByRankMap = Record<string, CourseData>;

interface RankingRow {
  lessonName: string | null;
  beforeCount: number | null;
  beforeRating: number | null;
  afterCount: number | null;
  afterRating: number | null;
  learningSwing: number | null;
  percentageIncrease: number | null;
  type: "lesson" | "dojo" | "detailed" | "skeleton";
}

interface NumericBodyOptions {
  width?: string;
  format?: (value: number) => string;
}

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

const transformCoursesToRankMap = (
  courses: Array<{ name?: string; rankName?: string }>
): CoursesByRankMap => {
  const rankMap: CoursesByRankMap = {};
  courses.forEach(course => {
    if (course.rankName && course.name) {
      rankMap[course.rankName] = { name: course.name, rankName: course.rankName };
    }
  });
  return rankMap;
};

const getProgressLevelOptions = (
  coursesByRank: CoursesByRankMap
): { label: string; value: string }[] =>
  Object.keys(coursesByRank).map(rank => {
    const courseData = coursesByRank[rank];
    const label =
      courseData?.name && courseData.name.trim().length > 0 ? courseData.name : rank.toUpperCase();
    return { label, value: rank };
  });

const transformRankingsData = (
  coursesByRank: CoursesByRankMap,
  lessonLevels: Record<string, LessonLevelData>,
  dojoLevels: Record<string, DojoLevelData>,
  lessons: LessonData[]
): RankingRow[] => {
  const tableData: RankingRow[] = [];

  Object.entries(lessonLevels).forEach(([beltRank, levelData]) => {
    if (levelData && coursesByRank[beltRank]) {
      tableData.push({
        lessonName: coursesByRank[beltRank]?.name ?? null,
        beforeCount: levelData.beforeCount ?? 0,
        beforeRating: levelData.beforeRating ?? 0,
        afterCount: levelData.afterCount ?? 0,
        afterRating: levelData.afterRating ?? 0,
        learningSwing: (levelData.afterRating ?? 0) - (levelData.beforeRating ?? 0),
        percentageIncrease: levelData.percentIncrease ?? 0,
        type: "lesson",
      });
    }
  });

  Object.entries(dojoLevels).forEach(([beltRank, levelData]) => {
    if (levelData && coursesByRank[beltRank]) {
      const totalActivities =
        (levelData.launched?.count ?? 0) +
        (levelData.inProgress?.count ?? 0) +
        (levelData.passed?.count ?? 0);
      tableData.push({
        lessonName: coursesByRank[beltRank]?.name ?? null,
        beforeCount: 0,
        beforeRating: 0,
        afterCount: totalActivities,
        afterRating: 0,
        learningSwing: 0,
        percentageIncrease: 0,
        type: "dojo",
      });
    }
  });

  lessons.forEach(lesson => {
    tableData.push({
      lessonName: lesson.name ?? null,
      beforeCount: lesson.beforeCount ?? 0,
      beforeRating: lesson.beforeRating ?? 0,
      afterCount: lesson.afterCount ?? 0,
      afterRating: lesson.afterRating ?? 0,
      learningSwing: (lesson.afterRating ?? 0) - (lesson.beforeRating ?? 0),
      percentageIncrease: lesson.percentIncrease ?? 0,
      type: "detailed",
    });
  });

  return tableData;
};

const getLessonChartData = (lessonLevels: Record<string, LessonLevelData>) => {
  const entries = Object.entries(lessonLevels);
  const data: number[] = [];
  entries.forEach(([, levelData]) => {
    data.push(levelData.beforeRating ?? 0);
    data.push(levelData.afterRating ?? 0);
  });

  if (data.length === 0) {
    return {
      labels: ["No Data"],
      datasets: [
        {
          label: "Ratings",
          data: [0],
          backgroundColor: chartColors.getLessonChartColor(),
          borderWidth: 0,
        },
      ],
    };
  }

  return {
    labels: data.map(() => ""),
    datasets: [
      {
        label: "Ratings",
        data,
        backgroundColor: chartColors.getLessonChartColor(),
        borderWidth: 0,
        borderRadius: 2,
        ...barDatasetOptions,
      },
    ],
  };
};

const getDojoChartData = (_beltRank: string, statuses: DojoLevelData) => {
  const data = [
    statuses.launched?.count ?? 0,
    statuses.inProgress?.count ?? 0,
    statuses.passed?.count ?? 0,
  ];

  if (data.every(val => val === 0)) {
    return {
      labels: ["No Data"],
      datasets: [
        {
          label: "Activities",
          data: [0],
          backgroundColor: chartColors.getLessonChartColor(),
          borderWidth: 0,
        },
      ],
    };
  }

  return {
    labels: ["", "", ""],
    datasets: [
      {
        label: "Activities",
        data,
        backgroundColor: chartColors.getDojoChartColors(),
        borderWidth: 0,
        borderRadius: 2,
        ...barDatasetOptions,
      },
    ],
  };
};

const skeletonTemplateBody = (
  getValue: (rowData: RankingRow) => number | null,
  options: NumericBodyOptions = {}
) =>
  withSkeleton<RankingRow>(rowData => {
    const value = getValue(rowData) ?? 0;
    return <span>{options.format ? options.format(value) : value}</span>;
  }, options.width ?? "3rem");

// ---------------------------------------------------------------------------
// Skeleton chart card
// ---------------------------------------------------------------------------

const SkeletonChartCard = memo(() => (
  <Card className="p-4">
    <div className="mb-3">
      <Skeleton width="200px" height="20px" className="mb-2 block" />
      <Skeleton width="120px" height="16px" />
    </div>
    <div className="mb-3" style={{ height: "120px" }}>
      <Skeleton width="100%" height="120px" />
    </div>
    <div className="flex justify-between mt-10">
      {[0, 1, 2].map(i => (
        <div key={i} className="text-center flex-1">
          <Skeleton width="30px" height="16px" className="mb-1 block" />
          <Skeleton width="50px" height="12px" />
        </div>
      ))}
    </div>
  </Card>
));
SkeletonChartCard.displayName = "SkeletonChartCard";

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const RankingsReport = () => {
  useBreadcrumb({
    label: "Rankings Report",
    route: AdminAppRoutes.routes.reporting.routes.rankings.route,
    display: "none",
    resetTrail: true,
  });

  const currentTenantName = getCurrentSelectedTenant() ?? "";

  const { showToast } = useToast();
  const hideTimeSpent = useHideTimeSpent();

  const storedRows = useMemo(() => {
    try {
      const stored = window.localStorage.getItem(`rankingsReport-${currentTenantName}`);
      return stored
        ? ((JSON.parse(stored) as { rows?: number }).rows ?? DEFAULT_ROW_COUNT)
        : DEFAULT_ROW_COUNT;
    } catch {
      return DEFAULT_ROW_COUNT;
    }
  }, [currentTenantName]);

  const skeletonRowsData = useMemo(() => skeletonRows<RankingRow>(storedRows), [storedRows]);

  // Cached course metadata — fetched only once
  const coursesByRankRef = useRef<CoursesByRankMap>({});
  const [coursesByRank, setCoursesByRank] = useState<CoursesByRankMap>({});

  // Data state
  const [lessonLevels, setLessonLevels] = useState<Record<string, LessonLevelData>>({});
  const [dojoLevels, setDojoLevels] = useState<Record<string, DojoLevelData>>({});
  const [lessons, setLessons] = useState<LessonData[]>([]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filter / sort state
  const [dateRange, setDateRange] = useState<Date[] | null>(null);
  const [filters, setFilters] = useState({
    lessonName: { value: null as string | null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0 | null>(null);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load course metadata only once
      if (Object.keys(coursesByRankRef.current).length === 0) {
        const levelsData = await getLevels();
        const courses = levelsData?.courses ?? [];
        const map = transformCoursesToRankMap(courses);
        coursesByRankRef.current = map;
        setCoursesByRank(map);
      }

      const params: { from?: number; to?: number } = {};
      if (dateRange?.[0] && dateRange[1]) {
        params.from = Math.floor(dateRange[0].getTime() / 1000);
        params.to = Math.floor(dateRange[1].getTime() / 1000);
      }

      const [levelsVPData, lessonsData] = await Promise.all([
        loadLevelsVP(params),
        loadLessonsVP(params),
      ]);

      setLessonLevels(levelsVPData.lessonLevels ?? {});
      setDojoLevels(levelsVPData.dojoLevels ?? {});
      setLessons(lessonsData.lessons ?? []);
    } catch (error) {
      console.error("Failed to load rankings report:", error);
      showToast({ severity: "error", detail: "Failed to load rankings report" });
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const tableData = useMemo(
    () => transformRankingsData(coursesByRank, lessonLevels, dojoLevels, lessons),
    [coursesByRank, lessonLevels, dojoLevels, lessons]
  );

  const levelOptions = useMemo(() => getProgressLevelOptions(coursesByRank), [coursesByRank]);

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  const handleExportCSV = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const params: { from?: number; to?: number } = {};
      if (dateRange?.[0] && dateRange[1]) {
        params.from = Math.floor(dateRange[0].getTime() / 1000);
        params.to = Math.floor(dateRange[1].getTime() / 1000);
      }
      await exportRankingsCSV(params);
    } catch (error) {
      console.error("Failed to export rankings report:", error);
      showToast({ severity: "error", detail: "Failed to export rankings report" });
    } finally {
      setIsExporting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Column body templates (memoized)
  // -------------------------------------------------------------------------

  const lessonNameBody = withSkeleton<RankingRow>(row => row.lessonName ?? "--", "12rem");

  const beforeCountBody = useMemo(() => skeletonTemplateBody(rowData => rowData.beforeCount), []);
  const beforeRatingBody = useMemo(() => skeletonTemplateBody(rowData => rowData.beforeRating), []);
  const afterCountBody = useMemo(() => skeletonTemplateBody(rowData => rowData.afterCount), []);
  const afterRatingBody = useMemo(() => skeletonTemplateBody(rowData => rowData.afterRating), []);
  const learningSwingBody = useMemo(
    () =>
      skeletonTemplateBody(rowData => rowData.learningSwing, {
        format: value => value.toFixed(2),
      }),
    []
  );
  const percentageBody = useMemo(
    () =>
      skeletonTemplateBody(rowData => rowData.percentageIncrease, {
        width: "4rem",
        format: value => `${value > 0 ? "+" : ""}${value}%`,
      }),
    []
  );

  const lessonFilterTemplate = textFilterElement("Search modules...");

  // -------------------------------------------------------------------------
  // Column group header
  // -------------------------------------------------------------------------

  const headerGroup = (
    <ColumnGroup>
      <Row>
        <Column header="Lesson" rowSpan={2} />
        <Column header="Before Lesson" colSpan={2} />
        <Column header="After Lesson" colSpan={2} />
        <Column header="Learning Swing" colSpan={2} />
      </Row>
      <Row>
        <Column header="# Ratings" />
        <Column header="Avg. Rating" />
        <Column header="# Ratings" />
        <Column header="Avg. Rating" />
        <Column header="Avg. Rating" />
        <Column header="Percentage Increase" />
      </Row>
    </ColumnGroup>
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <>
      <div className="mb-4">
        <h1>Rankings Report</h1>
      </div>

      {/* Chart cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 mb-20">
        {isLoading ? (
          <>
            <SkeletonChartCard />
            <SkeletonChartCard />
            <SkeletonChartCard />
          </>
        ) : (
          <>
            {/* Learning Swing summary card */}
            <Card className="p-4 md:col-span-2 2xl:col-span-1">
              <div className="mb-3">
                <h3 className="atlas-h5 font-semibold mb-2">Learning Swing - Summary</h3>
                {(() => {
                  const entries = Object.entries(lessonLevels);
                  const total = entries.reduce(
                    (sum, [, data]) => sum + (data.percentIncrease ?? 0),
                    0
                  );
                  const avg = entries.length > 0 ? total / entries.length : 0;
                  return <p className="body-sm text-muted">Average: +{avg.toFixed(1)}%</p>;
                })()}
              </div>
              <div className="mb-3" style={{ height: "120px" }}>
                <Chart type="bar" data={getLessonChartData(lessonLevels)} options={chartOptions} />
              </div>
              <div className="flex justify-between mt-10">
                {Object.entries(lessonLevels).flatMap(([beltRank, data]) => [
                  <div key={`${beltRank}-before`} className="text-center flex-1">
                    <p className="body-sm text-muted">Before: {data.beforeRating ?? 0}</p>
                  </div>,
                  <div key={`${beltRank}-after`} className="text-center flex-1">
                    <p className="body-sm text-muted">After: {data.afterRating ?? 0}</p>
                  </div>,
                ])}
              </div>
              <div className="flex justify-between mt-2">
                {Object.entries(lessonLevels).map(([beltRank, data]) => (
                  <div key={beltRank} className="text-center" style={{ flex: "0 0 33.333%" }}>
                    <p>+{data.percentIncrease ?? 0}%</p>
                    <p className="body-sm text-muted">
                      {levelOptions.find(opt => opt.value === beltRank)?.label}
                    </p>
                  </div>
                ))}
              </div>
            </Card>

            {/* Dojo level cards */}
            {Object.entries(dojoLevels).map(([beltRank, statuses]) => {
              const totalHours =
                (statuses.launched?.points ?? 0) +
                (statuses.inProgress?.points ?? 0) +
                (statuses.passed?.points ?? 0);
              return (
                <Card key={beltRank} className="p-4">
                  <div className="mb-3">
                    <h3 className="atlas-h5 font-semibold mb-2">
                      {levelOptions.find(opt => opt.value === beltRank)?.label}
                    </h3>
                    {!hideTimeSpent && (
                      <p className="body-sm text-muted">Total Hours: {totalHours}</p>
                    )}
                  </div>
                  <div className="mb-3" style={{ height: "80px" }}>
                    <Chart
                      type="bar"
                      data={getDojoChartData(beltRank, statuses)}
                      options={chartOptions}
                    />
                  </div>
                  <div className="flex justify-between mt-20">
                    <div className="text-center flex-1">
                      <p>{statuses.launched?.count ?? 0}</p>
                      <p className="body-sm text-muted">Launched</p>
                    </div>
                    <div className="text-center flex-1">
                      <p>{statuses.inProgress?.count ?? 0}</p>
                      <p className="body-sm text-muted">In Progress</p>
                    </div>
                    <div className="text-center flex-1">
                      <p>{statuses.passed?.count ?? 0}</p>
                      <p className="body-sm text-muted">Completed</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </>
        )}
      </div>

      {/* Filters / export row */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex gap-2 items-center">
          <Calendar
            value={dateRange}
            onChange={e => setDateRange(e.value as Date[] | null)}
            selectionMode="range"
            dateFormat="mm/dd/yy"
            placeholder="Select date range"
            className="max-w-64"
            showButtonBar
            hideOnRangeSelection
          />
          {!isEmpty(dateRange) && (
            <Button
              label="Clear Filters"
              icon="pi pi-filter-slash"
              onClick={() => {
                setDateRange(null);
                setFilters({
                  lessonName: { value: null, matchMode: FilterMatchMode.CONTAINS },
                });
              }}
              size="small"
              severity="secondary"
            />
          )}
        </div>
        <Button
          label="Export data"
          icon="pi pi-download"
          onClick={handleExportCSV}
          disabled={isLoading || isExporting}
          loading={isExporting}
          data-testid="rankings-export-button"
        />
      </div>

      {/* Data table */}
      <DataTable
        value={(isLoading ? skeletonRowsData : tableData) as unknown as DataTableValueArray}
        size="normal"
        aria-label="Rankings report"
        data-testid="rankings-report-table"
        emptyMessage={RANKINGS_EMPTY_ROW_TEXT}
        paginator
        rows={storedRows}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        stateKey={`rankingsReport-${currentTenantName}`}
        stateStorage="local"
        sortMode="single"
        sortField={sortField ?? undefined}
        sortOrder={sortOrder}
        onSort={e => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder as 1 | -1 | 0 | null);
        }}
        filters={filters}
        onFilter={e => setFilters(e.filters as typeof filters)}
        removableSort
        headerColumnGroup={headerGroup}
        tableClassName="dt-responsive-text"
      >
        <Column
          field="lessonName"
          header="Module"
          sortable
          filter
          showFilterMatchModes={false}
          filterElement={lessonFilterTemplate}
          body={lessonNameBody}
          style={{ minWidth: "300px" }}
        />
        <Column
          field="beforeCount"
          header="# Ratings"
          sortable
          body={beforeCountBody}
          style={{ minWidth: "100px" }}
        />
        <Column
          field="beforeRating"
          header="Avg. Rating"
          sortable
          body={beforeRatingBody}
          style={{ minWidth: "100px" }}
        />
        <Column
          field="afterCount"
          header="# Ratings"
          sortable
          body={afterCountBody}
          style={{ minWidth: "100px" }}
        />
        <Column
          field="afterRating"
          header="Avg. Rating"
          sortable
          body={afterRatingBody}
          style={{ minWidth: "100px" }}
        />
        <Column
          field="learningSwing"
          header="Avg. Rating"
          sortable
          body={learningSwingBody}
          style={{ minWidth: "120px" }}
        />
        <Column
          field="percentageIncrease"
          header="Percentage Increase"
          sortable
          body={percentageBody}
          style={{ minWidth: "120px" }}
        />
      </DataTable>
    </>
  );
};

export default RankingsReport;
